import React, { useState, useEffect, useRef } from 'react';
import { VolumeX } from 'lucide-react';
import { useVoicePreset, speakWithPreset } from '../utils/voicePresets';

/**
 * Inline button that reads provided `text` aloud using the user's chosen
 * kawaii voice preset (Web Speech API). Free, no API key.
 */
export default function SpeakButton({ text, label = 'Läs upp', className = '', size = 'sm' }) {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const [preset] = useVoicePreset();
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setSupported(false);
    }
    return () => {
      if (window.speechSynthesis?.speaking) window.speechSynthesis.cancel();
    };
  }, []);

  const speak = () => {
    if (!supported) return;
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    setSpeaking(true);
    cleanupRef.current = speakWithPreset(trimmed, preset, () => setSpeaking(false));
  };

  if (!supported) return null;

  const sizeCls = size === 'xs' ? 'p-1 text-[10px]' : 'p-1.5 text-xs';

  return (
    <button
      onClick={speak}
      className={`neo-btn ${sizeCls} flex items-center gap-1 ${speaking ? 'bg-[#FFC8DD]' : 'bg-white'} ${className}`}
      title={speaking ? 'Stoppa uppläsning' : `${label} (${preset.emoji} ${preset.label})`}
      data-testid="speak-button"
      type="button"
    >
      {speaking ? (
        <>
          <VolumeX className="w-3 h-3" />
          {label && <span>Stoppa</span>}
        </>
      ) : (
        <>
          <span className="text-[11px]">{preset.emoji}</span>
          {label && <span>{label}</span>}
        </>
      )}
    </button>
  );
}
